import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Video, Clock3, MessageCircle, Maximize2, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, SectionHeading } from "./Reveal";
import { GALLERY_MEDIA, GALLERY_SLOTS, waLink, API } from "@/lib/site";

const CATEGORIES = ["All", "Morning Batches", "Wellness Camps", "Community Sessions"];

export const Gallery = () => {
  const [category, setCategory] = useState("All");
  const [active, setActive] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/media`)
      .then(({ data }) => setUploaded(data))
      .catch(() => {});
  }, []);

  const dynamicMedia = uploaded
    .filter((m) => m.kind.startsWith("gallery"))
    .map((m) => ({
      id: m.id,
      category: m.category || "Community Sessions",
      type: m.kind.endsWith("video") ? "video" : "photo",
      label: m.label || "NAVYOGA Session",
      src: `${API}/media/file/${m.id}`,
    }));

  const allMedia = [...dynamicMedia, ...GALLERY_MEDIA];
  const media = category === "All" ? allMedia : allMedia.filter((m) => m.category === category);
  const slots = category === "All" ? GALLERY_SLOTS : GALLERY_SLOTS.filter((s) => s.category === category);

  return (
    <section id="classes" data-testid="group-classes-gallery-section" className="relative bg-white py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading
          testId="gallery-heading"
          overline="Photos & Videos"
          title="Our Group Classes in Action"
          description="Real moments from our batches, wellness camps and community sessions — photographed and uploaded personally by Dr. Rajni Ahuja. More memories are being added soon."
        />

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Gallery categories">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`gallery-tab-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(cat)}
                className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  category === cat ? "text-white" : "text-slate-600 hover:text-[#2E8B57]"
                }`}
                role="tab"
                aria-selected={category === cat}
              >
                {category === cat && (
                  <motion.span
                    layoutId="gallery-tab-pill"
                    className="absolute inset-0 rounded-full bg-[#1E3A8A]"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
                <span className="relative">{cat}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {media.map((item) => (
              <motion.button
                layout
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`gallery-media-${item.id}`}
                onClick={() => setViewer(item)}
                className="group relative block aspect-[4/3] overflow-hidden rounded-3xl shadow-[0_18px_40px_-20px_rgba(30,58,138,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5"
                aria-label={`View: ${item.label}`}
              >
                {item.type === "video" ? (
                  <>
                    <video src={item.src} muted playsInline preload="metadata" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/25 text-white backdrop-blur transition-transform duration-300 group-hover:scale-110">
                        <Play className="h-6 w-6 fill-current" />
                      </span>
                    </span>
                  </>
                ) : (
                  <img
                    src={item.src}
                    alt={item.label}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                <span className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" aria-hidden="true" />
                <span className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-5">
                  <span className="text-left">
                    <span className="block text-sm font-semibold text-white">{item.label}</span>
                    <span className="mt-0.5 block text-xs text-white/70">{item.category}</span>
                  </span>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-transform duration-300 group-hover:scale-110">
                    <Maximize2 className="h-4 w-4" />
                  </span>
                </span>
              </motion.button>
            ))}

            {slots.map((slot) => (
              <motion.button
                layout
                key={slot.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`gallery-slot-${slot.id}`}
                onClick={() => setActive(slot)}
                className="group relative flex aspect-[4/3] flex-col items-center justify-center overflow-hidden rounded-3xl border-2 border-dashed border-[#2E8B57]/30 bg-[#F8FFF8] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-[#2E8B57]/60 hover:shadow-[0_24px_50px_-20px_rgba(46,139,87,0.3)]"
              >
                <div className="glow-emerald absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#2E8B57] shadow-sm ring-1 ring-emerald-900/10">
                  {slot.type === "video" ? <Video className="h-6 w-6" /> : <Camera className="h-6 w-6" />}
                </div>
                <p className="relative mt-4 text-sm font-semibold text-slate-800">{slot.label}</p>
                <p className="relative mt-1 text-xs text-slate-500">{slot.category}</p>
                <span className="relative mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#1E3A8A]/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A]">
                  <Clock3 className="h-3 w-3" />
                  {slot.type === "video" ? "Video" : "Photo"} upload pending
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal delay={0.15}>
          <p className="mt-8 text-sm italic text-slate-500">
            We never use stock photographs — every frame here is a real NAVYOGA session.
          </p>
        </Reveal>
      </div>

      <Dialog open={!!viewer} onOpenChange={() => setViewer(null)}>
        <DialogContent data-testid="gallery-photo-viewer-modal" className="max-w-3xl rounded-3xl border-emerald-900/10 bg-white p-4">
          {viewer && (
            <>
              <DialogTitle className="sr-only">{viewer.label}</DialogTitle>
              {viewer.type === "video" ? (
                <video src={viewer.src} controls autoPlay playsInline className="max-h-[75vh] w-full rounded-2xl" />
              ) : (
                <img src={viewer.src} alt={viewer.label} className="max-h-[75vh] w-full rounded-2xl object-contain" />
              )}
              <p className="px-2 pb-2 pt-3 text-center text-sm font-semibold text-slate-800">
                {viewer.label} · {viewer.category}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="gallery-lightbox-modal" className="max-w-md rounded-3xl border-emerald-900/10 bg-[#F8FFF8] p-8">
          {active && (
            <>
              <DialogHeader>
                <div className="flex aspect-video items-center justify-center rounded-2xl border-2 border-dashed border-[#2E8B57]/30 bg-white">
                  {active.type === "video" ? (
                    <Video className="h-10 w-10 text-[#2E8B57]/50" />
                  ) : (
                    <Camera className="h-10 w-10 text-[#2E8B57]/50" />
                  )}
                </div>
                <DialogTitle className="font-heading mt-5 text-2xl font-semibold text-slate-900">{active.label}</DialogTitle>
                <DialogDescription className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
                  This space is reserved for a real {active.type} from our <strong>{active.category}</strong>, uploaded
                  personally by the studio. Until then, come experience it live — seats are open.
                </DialogDescription>
              </DialogHeader>
              <a
                data-testid="gallery-modal-whatsapp-cta"
                href={waLink(`Namaste! I would like to join the ${active.category} at NAVYOGA.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#2E8B57] px-6 py-3.5 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#267a4b]"
              >
                <MessageCircle className="h-4 w-4" />
                Ask About This Batch
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
